import { useEffect, useState } from "react";
import { Upload, X, Save } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { web, errMsg } from "../../lib/api";
import { PageHeader, Panel, Loader, ErrorNote, Btn } from "../../components/app/kit";
import { ProspectCard, DEFAULT_BRAND } from "../../components/app/ProspectCard";

const SAMPLE = {
  player: { name: "Sample Prospect", jersey: "0", position: "SG", grad_year: 2027, height_in: 75, gpa: 3.5 },
  team_name: "Sample High", tag: "Top Target",
  season: { ppg: 18.4, rpg: 5.1, apg: 3.2, ts_pct: 58.0, fg_pct: 47.5, fg3_pct: 36.2, ft_pct: 81.0, gp: 12 },
  zone_pct: { rim: 64, paint: 52, midrange: 41, corner3: 39, wing3: 34, top3: 37 },
  comparison: { label: "Shooting Guard" },
};
const BG_PRESETS = ["#0A0E1A", "#111111", "#0B1F3A", "#2A0A0A", "#0A2414", "#1F1033"];

const lum = (hex) => {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};
const contrast = (a, b) => { const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m); return (x + 0.05) / (y + 0.05); };
const isHex = (v) => /^#[0-9a-fA-F]{6}$/.test(v);

const resizeLogo = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 320 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k);
    c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    const png = c.toDataURL("image/png");
    resolve(png.length < 380000 ? png : c.toDataURL("image/webp", 0.9));
    URL.revokeObjectURL(img.src);
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

const ColorField = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="mb-2 block font-mono-gd text-[11px] uppercase tracking-[0.25em] text-slate-400">{label}</span>
    <div className="flex items-center gap-3">
      <input type="color" data-testid={`${testid}-picker`} value={isHex(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-11 w-14 cursor-pointer rounded-lg border border-[#1E293B] bg-transparent" />
      <input data-testid={`${testid}-input`} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} maxLength={7} className="w-32 rounded-xl border border-[#1E293B] bg-[#0A0E1A] px-3 py-2.5 font-mono-gd text-sm text-white outline-none focus:border-[#39FF14]" />
    </div>
  </label>
);

export default function Branding() {
  const { data, loading, error } = useApi("/branding", web);
  const [form, setForm] = useState(DEFAULT_BRAND);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setForm({ ...DEFAULT_BRAND, ...data, school_name: data.school_name ?? "" });
  }, [data]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = isHex(form.accent) && isHex(form.background);
  const tooLight = valid && lum(form.background) > 0.12;
  const lowContrast = valid && contrast(form.accent, form.background) < 3;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/image\/(png|jpeg|webp)/.test(f.type)) { toast.error("Use a PNG, JPG or WebP logo"); return; }
    try { set("logo")(await resizeLogo(f)); } catch { toast.error("Couldn't read that image"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await web.put("/branding", { school_name: form.school_name || null, logo: form.logo || null, accent: form.accent, background: form.background });
      toast.success("Card branding saved to the website and the mobile app");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <Loader />;
  if (error) return <ErrorNote error={error} />;

  return (
    <div>
      <PageHeader eyebrow="Recruiter" title="Card branding">
        <Btn data-testid="branding-save-button" onClick={save} disabled={saving || !valid || tooLight || lowContrast}><Save className="h-4 w-4" />{saving ? "Saving…" : "Save branding"}</Btn>
      </PageHeader>
      <p data-testid="branding-sync-note" className="-mt-4 mb-8 max-w-2xl text-sm text-slate-400">Your branding is shared with the GAMEDAY mobile app, so prospect cards look the same on the website and in the app. Changes in either place show up in both.</p>
      <div className="grid gap-8 xl:grid-cols-[1fr_auto]">
        <Panel className="space-y-6">
          <label className="block">
            <span className="mb-2 block font-mono-gd text-[11px] uppercase tracking-[0.25em] text-slate-400">School / program name</span>
            <input data-testid="branding-school-input" value={form.school_name} maxLength={80} onChange={(e) => set("school_name")(e.target.value)} placeholder="e.g. State University Basketball" className="w-full rounded-xl border border-[#1E293B] bg-[#0A0E1A] px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-[#39FF14]" />
          </label>
          <div>
            <span className="mb-2 block font-mono-gd text-[11px] uppercase tracking-[0.25em] text-slate-400">School logo</span>
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-[#1E293B] bg-[#0A0E1A]">
                {form.logo ? <img src={form.logo} alt="School logo" data-testid="branding-logo-preview" className="h-full w-full rounded-2xl object-contain p-2" /> : <Upload className="h-5 w-5 text-slate-500" />}
              </div>
              <label className="cursor-pointer rounded-full border border-[#1E293B] bg-[#162032] px-5 py-2.5 text-sm font-semibold text-white hover:border-[#39FF14]/50">
                Upload logo
                <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="branding-logo-input" onChange={onFile} className="hidden" />
              </label>
              {form.logo && <button data-testid="branding-logo-remove" onClick={() => set("logo")(null)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-red-200"><X className="h-4 w-4" />Remove</button>}
            </div>
            <p className="mt-2 text-xs text-slate-400">PNG with a transparent background looks best. Resized to 320 px automatically.</p>
          </div>
          <div className="flex flex-wrap gap-8">
            <ColorField label="Accent colour" value={form.accent} onChange={set("accent")} testid="branding-accent" />
            <ColorField label="Background" value={form.background} onChange={set("background")} testid="branding-background" />
          </div>
          <div className="flex flex-wrap gap-2">
            {BG_PRESETS.map((c) => (
              <button key={c} data-testid={`branding-bg-preset-${c.slice(1)}`} onClick={() => set("background")(c)} aria-label={`Background ${c}`} className={`h-9 w-9 rounded-full border-2 ${form.background === c ? "border-[#39FF14]" : "border-[#1E293B]"}`} style={{ backgroundColor: c }} />
            ))}
          </div>
          {!valid && <p data-testid="branding-error" className="text-sm text-red-300">Colours must be hex values like #39FF14.</p>}
          {tooLight && <p data-testid="branding-error" className="text-sm text-red-300">Pick a darker background so the white text stays readable.</p>}
          {lowContrast && !tooLight && <p data-testid="branding-error" className="text-sm text-red-300">Your accent is too close to the background. Pick a brighter accent.</p>}
          <button data-testid="branding-reset" onClick={() => setForm((f) => ({ ...DEFAULT_BRAND, school_name: f.school_name, logo: f.logo }))} className="text-sm text-slate-400 underline hover:text-white">Reset colours to GAMEDAY</button>
        </Panel>
        <div>
          <p className="mb-3 font-mono-gd text-[11px] uppercase tracking-[0.25em] text-slate-400">Live preview · sample data</p>
          <div className="relative h-[405px] w-[324px] overflow-hidden rounded-2xl border border-[#1E293B] sm:h-[472px] sm:w-[378px]" data-testid="branding-preview">
            <div className="origin-top-left scale-[0.3] sm:scale-[0.35]">
              <ProspectCard data={SAMPLE} brand={valid ? form : DEFAULT_BRAND} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
